<script setup lang="ts">

import { computed, nextTick, ref, watch } from "vue";

import Datepicker from "@vuepic/vue-datepicker";
import moment from "moment-timezone";
import "@vuepic/vue-datepicker/dist/main.css";

// Documentation: https://vue3datepicker.com/

// Types
import { isset } from "@/helpers/object";

import type { DatePickerProps, DateRange } from "@/dsComponents/DatePicker/types";

// Components
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";

const props = withDefaults(defineProps<DatePickerProps>(), {
    size: "sm",
    placeholder: "",
    format: "dd MMMM yyyy",
    unactivate: false,
    variant: "stroke",
    autoApply: true,
    clearable: true,
    enableTimePicker: false,
    closeOnScroll: true,
    preventMinMaxNavigation: true,
    fullWidth: false,
});

const emit = defineEmits<{
    (e: "datePicked", value: string): void;
    (e: "rangePicked", value: DateRange): void;
    // For table alt
    (e: "click", value: string): void;
}>();

const selectedDate = ref<string | Date | Date[] | string[] | null>(null);
const ignoreNextPick = ref(false);

const getDate = (value: string) => {
    const momentDate = moment(value, props.format, true);
    if (momentDate.isValid()) {
        return momentDate.format(props.format);
    }
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        return moment(date).format(props.format);
    }

    return props.value;
};

const arrayToDateRange = (array: Date[] | string[]): DateRange => {
    return {
        start: array[0],
        end: array[1],
    };
};

const dateRangeToArray = (dateRange: DateRange): Date[] => {
    const start = moment(dateRange.start, props.format).toDate();
    const end = moment(dateRange.end, props.format).toDate();
    return [start, end];
};

watch(
    () => props.value,
    async () => {
        ignoreNextPick.value = true;
        if (!props.value) {
            selectedDate.value = null;
        } else if (props.range && typeof props.value === "object") {
            selectedDate.value = dateRangeToArray(props.value as DateRange);
        } else {
            selectedDate.value = moment(props.value as string | Date, props.format).toDate();
        }
        await nextTick();
        ignoreNextPick.value = false;
    },
    { immediate: true }
);

const onPick = () => {
    if (ignoreNextPick.value) return;

    if (props.range && Array.isArray(selectedDate.value)) {
        emit("rangePicked", arrayToDateRange(selectedDate.value));
        return;
    }

    emit("datePicked", selectedDate.value ? moment(selectedDate.value as string | Date).format(props.format) : "");
    emit("click", selectedDate.value ? moment(selectedDate.value as string | Date).format(props.format) : "");
};

const label = computed(() => {
    if (props.value) {
        if (props.range && typeof props.value === "object") {
            // biome-ignore lint/style/useTemplate: Init Biome
            return moment(props.value.start).format(props.format) + " - " + moment(props.value.end).format(props.format);
        } else {
            return getDate(props.value as string);
        }
    }
    return props.placeholder;
});

const isClearable = computed(() => {
    return props.clearable && isset(selectedDate.value);
});

const handleClear = () => {
    selectedDate.value = null;
    emit("datePicked", "");
    emit("click", "");
};

const handleOpen = () => {
    document.dispatchEvent(new CustomEvent("dropdown-close"));
};
</script>
<template>
    <Datepicker
        v-model="selectedDate"
        :range="range"
        :min-date="minDate"
        :max-date="maxDate"
        :start-date="startDate"
        :disabled="disabled"
        :auto-apply="autoApply"
        :menu-class-name="menuClassName"
        :clearable="clearable"
        :enable-time-picker="enableTimePicker"
        :close-on-scroll="closeOnScroll"
        :prevent-min-max-navigation="preventMinMaxNavigation"
        locale="fr"
        class="ds-date-picker"
        teleport
        @open="handleOpen"
        @update:model-value="onPick"
    >
        <template #trigger>
            <slot>
                <ComboButton
                    type="neutral"
                    :disabled="disabled"
                    :iconLeft="icon"
                    :size="size"
                    :variant="variant"
                    :focused="isset(value) && !unactivate"
                    :placeholder="!isset(value)"
                    :error="error"
                    :fullWidth="fullWidth"
                    :icon-right="isClearable ? 'x' : undefined"
                    @right="handleClear"
                >
                    {{ label }}
                </ComboButton>
            </slot>
        </template>
    </Datepicker>
</template>
<style>
/* Popover margin top */
.vd-menu__content {
    margin-top: 4px;
}

.ds-date-picker {
    width: 100%;
}
</style>
